import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Layers, FileText, Receipt, Download, ChevronDown, ChevronRight, CalendarDays } from "lucide-react";
import GroupStatusBadge from "@/components/admin/GroupStatusBadge";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
};
const formatMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

/**
 * Client-side SGM Web groups view. Shows each group with its invoices history
 * (sorted desc by date) and embedded complementos de pago. Each PDF is downloadable.
 */
export default function ClientSGMGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});  // { [groupId]: bool }

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/sgm-groups");
      setGroups(data || []);
      // Auto-expand the first group with invoices
      const first = (data || []).find((g) => (g.invoices_history || []).length > 0);
      if (first) setExpanded({ [first.id]: true });
    } catch {
      // silent — section just stays empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const downloadInvoice = async (group, inv) => {
    try {
      const res = await api.get(`/client/sgm-groups/${group.id}/invoices/${inv.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = inv.original_name || `factura-${inv.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar factura"); }
  };

  const downloadComplemento = async (group, inv, comp) => {
    try {
      const res = await api.get(`/client/sgm-groups/${group.id}/invoices/${inv.id}/complementos/${comp.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = comp.original_name || `complemento-${comp.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar complemento"); }
  };

  if (loading) {
    return (
      <div className="mt-12 border border-[#2E2E33] bg-[#1F1F25] p-8 text-center" data-testid="client-sgm-groups-loading">
        <p className="font-mono text-xs text-[#71717A] uppercase tracking-widest">// Cargando grupos SGM Web...</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return null;  // hide section entirely when the client has no groups
  }

  return (
    <section className="mt-12" data-testid="client-sgm-groups">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="font-heading font-bold uppercase text-xl sm:text-2xl tracking-tight">
          <Layers className="inline w-6 h-6 text-[#F59E0B] mr-2 -mt-1" />
          Grupos SGM Web · Facturas & Complementos
        </h2>
        <span className="font-mono text-[10px] sm:text-xs text-[#71717A] uppercase tracking-widest">[ {groups.length} GRUPO{groups.length === 1 ? "" : "S"} ]</span>
      </div>

      <div className="space-y-4">
        {groups.map((g) => {
          const hist = [...(g.invoices_history || [])].sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
          const totalInv = hist.reduce((s, i) => s + (Number(i.amount) || 0), 0);
          const totalPay = hist.reduce((s, i) => s + (i.complementos || []).reduce((ss, c) => ss + (Number(c.payment_amount) || 0), 0), 0);
          const isOpen = !!expanded[g.id];
          return (
            <div key={g.id} className="border border-[#2E2E33] bg-[#1F1F25]" data-testid={`client-group-${g.id}`}>
              <button
                type="button"
                onClick={() => setExpanded((s) => ({ ...s, [g.id]: !isOpen }))}
                className="w-full text-left px-4 sm:px-6 py-4 flex items-center justify-between gap-3 hover:bg-[#26262C] transition-colors"
                data-testid={`client-group-toggle-${g.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-[#F59E0B] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#F59E0B] shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-heading font-bold text-white truncate">{g.name}</div>
                      <GroupStatusBadge financial={g.financial} compact />
                    </div>
                    <div className="font-mono text-[10px] text-[#71717A] uppercase tracking-widest mt-0.5">
                      // {hist.length} factura{hist.length === 1 ? "" : "s"} · Facturado {formatMoney(totalInv)} · Pagado {formatMoney(totalPay)}
                    </div>
                  </div>
                </div>
                <div className="text-right hidden sm:block">
                  <div className="font-heading font-bold text-[#F59E0B] text-lg">{formatMoney(totalInv - totalPay)}</div>
                  <div className="font-mono text-[9px] text-[#71717A] uppercase tracking-widest">Saldo</div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-[#2E2E33] p-4 sm:p-6 space-y-3">
                  {hist.length === 0 ? (
                    <p className="font-mono text-xs text-[#71717A] uppercase tracking-widest text-center py-6">
                      // Aún no hay facturas en este grupo
                    </p>
                  ) : hist.map((inv) => {
                    const comps = inv.complementos || [];
                    return (
                      <div key={inv.id} className="border border-[#2E2E33] bg-[#1A1A1F]" data-testid={`client-invoice-${inv.id}`}>
                        <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText className="w-4 h-4 text-[#F59E0B] shrink-0" />
                              <span className="font-mono text-sm text-white truncate">
                                {inv.invoice_number ? <span className="text-[#F59E0B]">{inv.invoice_number} · </span> : null}
                                {inv.original_name || inv.filename}
                              </span>
                            </div>
                            <div className="font-mono text-[10px] text-[#A1A1AA] uppercase tracking-widest pl-6 flex items-center gap-2 flex-wrap">
                              <CalendarDays className="w-3 h-3 text-[#71717A]" /> {formatDate(inv.uploaded_at)}
                              <span>·</span>
                              <span className="text-[#F59E0B]">{formatMoney(inv.amount)}</span>
                              {inv.period && <><span>·</span><span>{inv.period}</span></>}
                            </div>
                            {inv.description && <div className="text-[#D4D4D8] text-xs mt-1.5 pl-6 italic">{inv.description}</div>}
                          </div>
                          <button
                            onClick={() => downloadInvoice(g, inv)}
                            className="inline-flex items-center gap-1.5 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest"
                            data-testid={`client-download-invoice-${inv.id}`}
                          >
                            <Download className="w-3.5 h-3.5" /> Factura PDF
                          </button>
                        </div>
                        {comps.length > 0 && (
                          <div className="border-t border-[#2E2E33] bg-[#141418]/60 px-4 py-3 space-y-2">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5 mb-1">
                              <Receipt className="w-3 h-3" /> {comps.length} Complemento{comps.length === 1 ? "" : "s"} de Pago
                            </div>
                            {comps.map((c) => (
                              <div key={c.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`client-complemento-${c.id}`}>
                                <div className="font-mono text-[11px] text-[#D4D4D8] min-w-0 flex-1">
                                  <span className="text-emerald-400">{c.original_name || c.filename}</span>
                                  <div className="text-[10px] text-[#71717A] mt-0.5 uppercase tracking-widest">
                                    {c.payment_date || formatDate(c.uploaded_at)} · {formatMoney(c.payment_amount)} · {c.payment_method || "—"}
                                  </div>
                                  {c.notes && <div className="text-[10px] text-[#A1A1AA] mt-1 italic">{c.notes}</div>}
                                </div>
                                <button
                                  onClick={() => downloadComplemento(g, inv, c)}
                                  className="inline-flex items-center gap-1.5 border border-emerald-400/50 hover:bg-emerald-400/10 text-emerald-400 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest"
                                  data-testid={`client-download-complemento-${c.id}`}
                                >
                                  <Download className="w-3 h-3" /> Complemento
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
